import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { tickets } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import {
  Alert, DataTable, EmptyState, KpiFilterRow, PageHeader, TableShell, Tabs
} from '@repletoryreach/sdk-ui/data-display';
import { Checkbox, Select, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { HelpButton } from '../../app/guides/HelpButton';
import { useT } from '../../i18n/I18nProvider';
import { formatCount, formatDateTime, formatRelative } from '../../lib/format';
import type { Indexed } from '../../lib/types';
import { usePersistedState } from '../../lib/usePersistedState';
import { useNow } from '../inbox/components/SlaBadge';
import { CreateTicketModal } from './components/CreateTicketModal';
import { PriorityTag, StatusTag, TicketNumber, TicketSlaBadge, TypeTag } from './components/TicketBits';
import {
  TICKETS_KEY, categoryLabel, categoryTone, personName, statusesForType, ticketErrorText, useLookups
} from './lib/data';
import './tickets.css';

const PAGE_SIZE = 50;

export default function TicketsQueuePage() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const now = useNow(30_000);
  const lk = useLookups();

  const [view, setView] = usePersistedState<tickets.TicketView>('tenant.tickets.view', 'all');
  const [category, setCategory] = useState<tickets.StatusCategory | ''>('');
  const [statusId, setStatusId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [priorityId, setPriorityId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [assignee, setAssignee] = useState('');
  const [tag, setTag] = useState('');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const q = useDebounce(search, 250);
  const tagQ = useDebounce(tag, 300);

  const filters = useMemo<tickets.TicketListQuery>(() => ({
    view,
    category: category || undefined,
    status_id: statusId || undefined,
    type_id: typeId || undefined,
    priority_id: priorityId || undefined,
    team_id: teamId || undefined,
    assignee_user_id: assignee || undefined,
    tag: tagQ.trim() || undefined,
    q: q.trim() || undefined
  }), [view, category, statusId, typeId, priorityId, teamId, assignee, tagQ, q]);
  const filtered = !!(category || statusId || typeId || priorityId || teamId || assignee || tagQ.trim() || q.trim());

  const list = useInfiniteQuery({
    queryKey: [TICKETS_KEY, 'list', filters],
    queryFn: ({ pageParam }) => tickets.listTickets({ ...filters, limit: PAGE_SIZE, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta?.next_cursor ?? undefined,
    refetchInterval: 60_000
  });
  const rows = (list.data?.pages.flatMap((p) => p.data) ?? []) as Indexed<tickets.TicketSummary>[];
  const counts = list.data?.pages[0]?.meta?.counts;

  function clearFilters() {
    setCategory('');
    setStatusId('');
    setTypeId('');
    setPriorityId('');
    setTeamId('');
    setAssignee('');
    setTag('');
    setSearch('');
  }

  function toggle(id: string, on: boolean) {
    setSelected((s) => {
      const next = new Set(s);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }

  /** The same change on every chosen ticket; a refusal on one does not stop the others. */
  async function bulk(patch: tickets.UpdateTicketRequest) {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkBusy(true);
    const results = await Promise.allSettled(ids.map((id) => tickets.updateTicket(id, patch)));
    setBulkBusy(false);
    const failed = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
    void qc.invalidateQueries({ queryKey: [TICKETS_KEY] });
    if (failed.length) notifyError(t('tickets.bulk.someFailed', { failed: failed.length, total: ids.length, reason: ticketErrorText(t, failed[0]!.reason) }), t('tickets.title'));
    else notifySuccess(t('tickets.bulk.done', { count: ids.length }), t('tickets.title'));
    setSelected(new Set());
  }

  const people = [
    ...(user ? [{ value: user.id, label: t('tickets.me') }] : []),
    ...lk.people.filter((p) => p.id !== user?.id).map((p) => ({ value: p.id, label: personName(p) }))
  ];
  // Bulk status: the statuses every chosen ticket's type may take.
  const chosen = rows.filter((r) => selected.has(r.id));
  const bulkStatuses = lk.statuses.filter((s) => chosen.every((r) => statusesForType(lk.statuses, r.type.id).some((x) => x.id === s.id)));
  const allChosen = rows.length > 0 && rows.every((r) => selected.has(r.id));

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('tickets.title')}
        description={t('tickets.description')}
        withDivider={false}
        actions={(
          <Group gap="sm">
            <HelpButton guide="tickets" />
            <Button size="sm" variant="default" onClick={() => nav('/tickets/board')}>{t('tickets.board.title')}</Button>
            <Button size="sm" data-guide="tickets-new" onClick={() => setCreating(true)}>{t('tickets.create.title')}</Button>
          </Group>
        )}
      />

      <div data-guide="tickets-views">
        <Tabs value={view} onChange={(v) => { setView(v as tickets.TicketView); setSelected(new Set()); }}>
          <Tabs.List>
            {tickets.TICKET_VIEWS.map((v) => <Tabs.Tab key={v} value={v}>{t(`tickets.view.${v}`)}</Tabs.Tab>)}
          </Tabs.List>
        </Tabs>
      </div>

      <TableShell
        data-guide="tickets-list"
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t('tickets.queue.search')}
        labels={{
          table: t('table.view.table'),
          cards: t('table.view.cards'),
          list: t('table.view.list'),
          search: t('table.search'),
          perPage: t('table.perPage'),
          pageSizeOption: (n) => t('table.perPageOption', { n }),
          empty: t('table.noRecords'),
          range: (a, b, total) => t('table.range', { from: a, to: b, total })
        }}
        kpis={(
          <KpiFilterRow
            value={category || null}
            onChange={(next) => setCategory((next ?? '') as tickets.StatusCategory | '')}
            filteringLabel={t('tickets.queue.filtering')}
            items={tickets.STATUS_CATEGORIES.map((c) => ({
              id: c,
              label: categoryLabel(t, c),
              value: counts ? formatCount(counts[c] ?? 0) : list.isPending ? '' : formatCount(0),
              tone: categoryTone(c)
            }))}
          />
        )}
        filters={(
          <Group gap="xs" wrap="wrap" align="flex-end" data-guide="tickets-filters">
            <Select size="sm" aria-label={t('common.status')} value={statusId} onChange={(e) => setStatusId(e.currentTarget.value)}
              data={[{ value: '', label: t('tickets.filter.anyStatus') }, ...lk.statuses.map((s) => ({ value: s.id, label: `${s.name} (${categoryLabel(t, s.category)})` }))]} />
            <Select size="sm" aria-label={t('tickets.field.type')} value={typeId} onChange={(e) => setTypeId(e.currentTarget.value)}
              data={[{ value: '', label: t('tickets.filter.anyType') }, ...lk.types.map((x) => ({ value: x.id, label: x.name }))]} />
            <Select size="sm" aria-label={t('tickets.field.priority')} value={priorityId} onChange={(e) => setPriorityId(e.currentTarget.value)}
              data={[{ value: '', label: t('tickets.filter.anyPriority') }, ...lk.priorities.map((p) => ({ value: p.id, label: p.name }))]} />
            {lk.teams.length > 0 && (
              <Select size="sm" aria-label={t('tickets.field.team')} value={teamId} onChange={(e) => setTeamId(e.currentTarget.value)}
                data={[{ value: '', label: t('tickets.filter.anyTeam') }, ...lk.teams.map((x) => ({ value: x.id, label: x.name }))]} />
            )}
            <Select size="sm" aria-label={t('tickets.field.assignee')} value={assignee} onChange={(e) => setAssignee(e.currentTarget.value)}
              data={[{ value: '', label: t('tickets.filter.anyone') }, ...people]} />
            <TextInput size="sm" aria-label={t('tickets.field.tag')} placeholder={t('tickets.filter.tag')} value={tag} onChange={(e) => setTag(e.currentTarget.value)} maxLength={60} />
          </Group>
        )}
        belowToolbar={selected.size > 0 ? (
          <Group gap="sm" wrap="wrap" className="rr-tickets__bulk" data-testid="tickets-bulk">
            <Text role="bodySmall" fw={600}>{t('tickets.bulk.selected', { count: selected.size })}</Text>
            <Select size="xs" aria-label={t('tickets.bulk.assign')} value="" disabled={bulkBusy}
              onChange={(e) => { const v = e.currentTarget.value; if (v) void bulk({ assignee_user_id: v === '-' ? null : v }); }}
              data={[{ value: '', label: t('tickets.bulk.assign') }, { value: '-', label: t('tickets.unassigned') }, ...people]} />
            <Select size="xs" aria-label={t('tickets.bulk.status')} value="" disabled={bulkBusy || bulkStatuses.length === 0}
              onChange={(e) => { const v = e.currentTarget.value; if (v) void bulk({ status_id: v }); }}
              data={[{ value: '', label: t('tickets.bulk.status') }, ...bulkStatuses.map((s) => ({ value: s.id, label: s.name }))]} />
            <Button size="xs" variant="subtle" onClick={() => setSelected(new Set())}>{t('action.clear')}</Button>
          </Group>
        ) : undefined}
        loading={list.isPending}
        error={list.error ? (
          <Alert color="error" role="alert">
            <Group gap="sm" justify="space-between" wrap="wrap">
              <span>{ticketErrorText(t, list.error)}</span>
              <Button size="xs" variant="default" onClick={() => void list.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        ) : undefined}
        isEmpty={!list.isPending && rows.length === 0}
        empty={(
          <EmptyState
            title={filtered ? t('tickets.queue.emptyFiltered') : t(`tickets.queue.empty.${view}`)}
            description={filtered ? t('tickets.queue.emptyFilteredHint') : t('tickets.queue.emptyHint')}
            action={filtered
              ? <Button size="xs" variant="default" onClick={clearFilters}>{t('inbox.filters.clear')}</Button>
              : <Button size="xs" onClick={() => setCreating(true)}>{t('tickets.create.title')}</Button>}
          />
        )}
      >
        <DataTable<Indexed<tickets.TicketSummary>>
          records={rows}
          loading={list.isFetching && !list.isFetchingNextPage}
          idAccessor="id"
          onRowClick={(r) => nav(`/tickets/${encodeURIComponent(r.id)}`)}
          columns={[
            {
              accessor: 'select',
              title: (
                <Checkbox aria-label={t('tickets.bulk.selectAll')} checked={allChosen}
                  indeterminate={selected.size > 0 && !allChosen}
                  onChange={(on) => setSelected(on ? new Set(rows.map((r) => r.id)) : new Set())} />
              ),
              width: 36,
              render: (r) => (
                <span onClick={(e) => e.stopPropagation()}>
                  <Checkbox aria-label={t('tickets.bulk.selectOne', { number: r.number })} checked={selected.has(r.id)} onChange={(on) => toggle(r.id, on)} />
                </span>
              )
            },
            { accessor: 'number', title: t('tickets.field.number'), render: (r) => <TicketNumber number={r.number} /> },
            {
              accessor: 'subject',
              title: t('tickets.field.subject'),
              render: (r) => (
                <Stack gap={0} className="rr-tickets__subject-cell">
                  <Text role="bodySmall" fw={500} lineClamp={1}>{r.subject}</Text>
                  <Text role="caption" c="var(--rr-color-text-muted)" lineClamp={1}>
                    {r.contact?.name || r.contact?.ref || t('tickets.noClient')}
                    {r.tags?.length ? ` · ${r.tags.join(', ')}` : ''}
                  </Text>
                </Stack>
              )
            },
            { accessor: 'type', title: t('tickets.field.type'), render: (r) => <TypeTag type={r.type} /> },
            { accessor: 'status', title: t('common.status'), render: (r) => <StatusTag status={r.status} /> },
            { accessor: 'priority', title: t('tickets.field.priority'), render: (r) => <PriorityTag priority={r.priority} /> },
            {
              accessor: 'assignee_user_id',
              title: t('tickets.field.assignee'),
              render: (r) => (
                <Stack gap={0}>
                  <Text role="bodySmall">{r.assignee_user_id ? (r.assignee_user_id === user?.id ? t('tickets.me') : lk.personLabel(r.assignee_user_id) || t('common.unknown')) : t('tickets.unassigned')}</Text>
                  {r.team_id && <Text role="caption" c="var(--rr-color-text-muted)">{lk.teamName(r.team_id)}</Text>}
                </Stack>
              )
            },
            { accessor: 'sla', title: t('tickets.field.sla'), render: (r) => <TicketSlaBadge input={r} now={now} /> },
            {
              accessor: 'updated_at',
              title: t('common.updated'),
              render: (r) => <Text role="caption" title={formatDateTime(r.updated_at)}>{formatRelative(r.updated_at)}</Text>
            }
          ]}
        />
        {list.hasNextPage && (
          <Group justify="center" style={{ paddingTop: 8 }}>
            <Button size="xs" variant="default" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
              {t('action.loadMore')}
            </Button>
          </Group>
        )}
      </TableShell>

      <CreateTicketModal
        opened={creating}
        onClose={() => setCreating(false)}
        onCreated={(tk) => {
          notifySuccess(t('tickets.create.done', { number: tk.number }), t('tickets.title'));
          nav(`/tickets/${encodeURIComponent(tk.id)}`);
        }}
      />
    </Stack>
  );
}
